import { useEffect, useRef, useState } from 'react';
import { useAuth, useAuthHeader } from '@/hooks/useAuth';
import {
  deleteAnalysisDataExtractionJob,
  downloadAnalysisDataExtractionFile,
  listAnalysisDataExtractionJobs,
} from '@/lib/api/analysis-data-extraction';
import type {
  AnalysisDataExtractionJob,
  AnalysisDataExtractionMenuType,
} from '@/lib/types/feature/analysis-data-extraction';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, CheckCircle, Clock, Download, Loader2, Trash2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';

/** ポーリング間隔（ms）。既存実装（5秒ごとにジョブステータスをチェック）に合わせる */
const POLLING_INTERVAL_MS = 5000;

/** メニュー種別の表示名 */
const MENU_TYPE_LABELS: Record<string, string> = {
  weekly_store_product_group: '[週次/月次] 店舗×商品グループ実績抽出',
  daily_store_sku: '[日別] 店舗×SKU 予測/実績/在庫抽出',
  product_master: '商品マスタ抽出',
  store_sku_setting_values: '店舗×SKU 設定値リスト',
  store_sku_sales_dates: '店舗×SKU 初回/最終販売日・初回入庫日',
};

interface AnalysisDataExtractionJobsProps {
  account: {
    id: string;
    code: string;
    name: string;
  };
  /** 対象メニュー種別（既定 weekly_store_product_group。一覧はメニュー単位で取得する） */
  menuType?: AnalysisDataExtractionMenuType;
}

/**
 * 分析用データ取得ジョブの実行ログ一覧
 * running/pending のジョブがある間は一定間隔で一覧を再取得する
 */
export function AnalysisDataExtractionJobs({
  account,
  menuType = 'weekly_store_product_group',
}: AnalysisDataExtractionJobsProps) {
  const { getAuthHeader } = useAuthHeader();
  const { user } = useAuth();
  const [jobs, setJobs] = useState<AnalysisDataExtractionJob[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloadingJobs, setDownloadingJobs] = useState<Set<string>>(new Set());
  const [deletingJobs, setDeletingJobs] = useState<Set<string>>(new Set());

  // 現在表示中のアカウントコード（アカウント切替後に前アカウントの遅延レスポンスを破棄するために使う）
  const accountCodeRef = useRef(account.code);
  useEffect(() => {
    accountCodeRef.current = account.code;
  }, [account.code]);

  // ジョブ一覧を取得（silent: ポーリング時はローディング表示を出さない）
  const fetchJobs = async (options?: { silent?: boolean }) => {
    const requestCode = account.code;
    try {
      if (!options?.silent) setLoading(true);
      setError(null);
      const authHeaders = getAuthHeader();
      const jobList = await listAnalysisDataExtractionJobs(requestCode, authHeaders, menuType);
      // アカウント切替後に届いた前アカウントのレスポンスは反映しない
      if (accountCodeRef.current !== requestCode) return;
      setJobs(jobList);
    } catch {
      if (accountCodeRef.current !== requestCode) return;
      setError('ジョブ一覧の取得に失敗しました');
    } finally {
      if (!options?.silent) setLoading(false);
    }
  };

  useEffect(() => {
    setJobs([]);
    fetchJobs();
  }, [account.code]);

  // running/pending のジョブがある間は5秒ごとに一覧を再取得する
  const hasActiveJobs = jobs.some(
    (job) => job.job_status === 'pending' || job.job_status === 'running',
  );
  useEffect(() => {
    if (!hasActiveJobs) return;

    const intervalId = setInterval(() => {
      fetchJobs({ silent: true });
    }, POLLING_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [hasActiveJobs, account.code]);

  // 結果ファイルのダウンロード処理（署名URL取得→aタグclick）
  const handleFileDownload = async (jobId: string) => {
    if (downloadingJobs.has(jobId)) return;

    try {
      setDownloadingJobs((prev) => new Set(prev).add(jobId));
      setError(null);
      const authHeaders = getAuthHeader();
      const downloadInfo = await downloadAnalysisDataExtractionFile(
        account.code,
        jobId,
        authHeaders,
      );

      // 新しいタブでダウンロードURLを開く
      const link = document.createElement('a');
      link.href = downloadInfo.download_url;
      link.download = downloadInfo.file_name;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      setError('ファイルのダウンロードに失敗しました');
    } finally {
      setDownloadingJobs((prev) => {
        const newSet = new Set(prev);
        newSet.delete(jobId);
        return newSet;
      });
    }
  };

  // ジョブ削除処理
  const handleDeleteJob = async (jobId: string) => {
    if (deletingJobs.has(jobId)) return;

    try {
      setDeletingJobs((prev) => new Set(prev).add(jobId));
      setError(null);
      const authHeaders = getAuthHeader();
      await deleteAnalysisDataExtractionJob(account.code, jobId, authHeaders);

      toast.success('実行ログが正常に削除されました');

      // ジョブ一覧を再読み込み
      await fetchJobs();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'ジョブの削除に失敗しました';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setDeletingJobs((prev) => {
        const newSet = new Set(prev);
        newSet.delete(jobId);
        return newSet;
      });
    }
  };

  // ジョブステータスのバッジ表示
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <Badge variant="default" className="bg-green-500">
            <CheckCircle className="w-3 h-3 mr-1" />
            完了
          </Badge>
        );
      case 'running':
        return (
          <Badge variant="default" className="bg-blue-500">
            <Clock className="w-3 h-3 mr-1" />
            実行中
          </Badge>
        );
      case 'failed':
        return (
          <Badge variant="destructive">
            <XCircle className="w-3 h-3 mr-1" />
            失敗
          </Badge>
        );
      case 'pending':
        return (
          <Badge variant="secondary">
            <AlertCircle className="w-3 h-3 mr-1" />
            待機中
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // メニュー種別の表示名（未知の種別はそのまま表示する）
  const getMenuTypeLabel = (menuType: string) => MENU_TYPE_LABELS[menuType] ?? menuType;

  // 実行開始日時のフォーマット
  const formatExecutionStartTime = (dateTime?: string) => {
    if (!dateTime) return '-';
    try {
      const date = new Date(dateTime);
      const dateStr = format(date, 'yyyy-MM-dd', { locale: ja });
      const timeStr = format(date, 'HH:mm', { locale: ja });
      return { dateStr, timeStr };
    } catch {
      return '-';
    }
  };

  // 所要時間（実行開始〜終了の差分）のフォーマット
  const formatDuration = (startTime?: string, endTime?: string): string => {
    if (!startTime || !endTime) return '-';
    const durationMs = new Date(endTime).getTime() - new Date(startTime).getTime();
    if (Number.isNaN(durationMs) || durationMs < 0) return '-';

    const totalSeconds = Math.floor(durationMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) return `${hours}時間${minutes}分${seconds}秒`;
    if (minutes > 0) return `${minutes}分${seconds}秒`;
    return `${seconds}秒`;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          分析用データ取得 実行ログ
          <Button onClick={() => fetchJobs()} disabled={loading} size="sm">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            更新
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {loading && jobs.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            読み込み中...
          </div>
        ) : jobs.length === 0 ? (
          <div className="text-center py-8 text-gray-500">実行ログがありません</div>
        ) : (
          <div className="space-y-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>実行日時</TableHead>
                  <TableHead>メニュー</TableHead>
                  <TableHead>ステータス</TableHead>
                  <TableHead>実行者</TableHead>
                  <TableHead>所要時間</TableHead>
                  <TableHead>操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobs.map((job) => {
                  // 実行開始時刻が無い（Fargate起動失敗などで実行前に終了した）場合は作成日時で代替する
                  const executionTime = formatExecutionStartTime(
                    job.execution_start_time ?? job.created_at,
                  );
                  return (
                    <TableRow key={job.id}>
                      <TableCell className="text-sm">
                        {executionTime === '-' ? (
                          '-'
                        ) : (
                          <div>
                            <div>{executionTime.dateStr}</div>
                            <div>{executionTime.timeStr}</div>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>{getMenuTypeLabel(job.menu_type)}</TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {getStatusBadge(job.job_status)}
                          {job.job_status === 'failed' && job.error_message && (
                            <FailedJobErrorMessage message={job.error_message} />
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">{job.executed_by}</TableCell>
                      <TableCell>
                        {formatDuration(job.execution_start_time, job.execution_end_time)}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-4 items-center">
                          {job.job_status === 'completed' && job.result_files_name && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleFileDownload(job.id)}
                              disabled={downloadingJobs.has(job.id)}
                            >
                              {downloadingJobs.has(job.id) ? (
                                <Loader2 className="w-3 h-3 animate-spin mr-1" />
                              ) : (
                                <Download className="w-3 h-3 mr-1" />
                              )}
                              結果
                            </Button>
                          )}
                          {user?.email === job.executed_by && (
                            <JobDeleteDialog
                              job={job}
                              onDeleteJob={handleDeleteJob}
                              isDeleting={deletingJobs.has(job.id)}
                            />
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** エラーメッセージのプレビュー文字数（超過分は折りたたむ） */
const ERROR_MESSAGE_PREVIEW_LENGTH = 120;

// 失敗ジョブのエラーメッセージ表示（長文は折りたたみ）
function FailedJobErrorMessage({ message }: { message: string }) {
  const [expanded, setExpanded] = useState(false);
  const isLong = message.length > ERROR_MESSAGE_PREVIEW_LENGTH;
  const shown =
    expanded || !isLong ? message : `${message.slice(0, ERROR_MESSAGE_PREVIEW_LENGTH)}...`;

  return (
    <div className="max-w-md text-xs text-destructive whitespace-pre-wrap break-words">
      {shown}
      {isLong && (
        <Button
          variant="ghost"
          size="sm"
          className="ml-1 h-5 px-1 text-xs"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? '省略表示' : 'もっと見る'}
        </Button>
      )}
    </div>
  );
}

// ジョブ削除の確認ダイアログ
interface JobDeleteDialogProps {
  job: AnalysisDataExtractionJob;
  onDeleteJob: (jobId: string) => Promise<void>;
  isDeleting: boolean;
}

function JobDeleteDialog({ job, onDeleteJob, isDeleting }: JobDeleteDialogProps) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button size="sm" variant="destructive" disabled={isDeleting}>
          {isDeleting ? (
            <Loader2 className="w-3 h-3 animate-spin mr-1" />
          ) : (
            <Trash2 className="w-3 h-3 mr-1" />
          )}
          削除
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>実行ログの削除確認</AlertDialogTitle>
          <AlertDialogDescription>
            この実行ログを削除してもよろしいですか？
            <br />
            実行者: {job.executed_by}
            <br />
            <strong>この操作は取り消すことができません。</strong>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>キャンセル</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => onDeleteJob(job.id)}
            className="bg-red-600 hover:bg-red-700"
          >
            削除する
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
